import React from 'react';

import { X } from 'lucide-react';
import { FieldErrors, UseFieldArrayReturn, UseFormRegister, UseFormWatch } from 'react-hook-form';

export type PhoneLocationFormValue = {
  name: string;
  phoneCount: string;
};

export type PhoneHardwareFormValues = {
  phoneLocations: PhoneLocationFormValue[];
};

type PhoneHardwareFieldsProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  watch: UseFormWatch<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldArray: UseFieldArrayReturn<any, 'phoneLocations', 'id'>;
};

// Whole numbers only: phone handsets are never fractional, and the input's
// `min` attribute alone does not stop someone typing 0 or 1.5.
const phoneCountRules = {
  validate: (value: string) =>
    /^[1-9]\d*$/.test(value?.trim() ?? '') || 'Enter a positive whole number.',
};

const PhoneHardwareFields: React.FC<PhoneHardwareFieldsProps> = ({
  register,
  errors,
  watch,
  fieldArray,
}: PhoneHardwareFieldsProps) => {
  const { fields, append, remove } = fieldArray;

  const watchedLocations = (watch('phoneLocations') as PhoneLocationFormValue[]) || [];
  const totalPhones = watchedLocations.reduce(
    (total, location) => total + (Number(location?.phoneCount) || 0),
    0
  );

  const locationErrors = errors.phoneLocations as
    | Array<{ name?: { message?: string }; phoneCount?: { message?: string } }>
    | undefined;

  return (
    <fieldset className="mt-5">
      <legend className="form-legend">Phone hardware</legend>

      <div className="mt-3">
        <div className="sm:grid-cols-[1fr_8rem_2rem] grid grid-cols-[1fr_6rem_2rem] gap-2 text-xs text-gray-500">
          <span>Location</span>
          <span>Number of phones</span>
          <span />
        </div>

        {fields.map((field, index) => (
          <div
            key={field.id}
            className="sm:grid-cols-[1fr_8rem_2rem] grid grid-cols-[1fr_6rem_2rem] gap-2 mt-2"
          >
            <div>
              <input
                className="input w-full"
                placeholder="e.g. San Juan"
                aria-label={`Location ${index + 1}`}
                {...register(`phoneLocations.${index}.name`, {
                  required: 'Required.',
                  validate: (value: string) => !!value?.trim() || 'Required.',
                })}
              />
              {locationErrors?.[index]?.name && (
                <span className="text-xs text-red-600">
                  {String(locationErrors[index].name?.message)}
                </span>
              )}
            </div>
            <div>
              <input
                type="number"
                min="1"
                step="1"
                className="input w-full"
                aria-label={`Phones at location ${index + 1}`}
                {...register(`phoneLocations.${index}.phoneCount`, phoneCountRules)}
              />
              {locationErrors?.[index]?.phoneCount && (
                <span className="text-xs text-red-600">
                  {String(locationErrors[index].phoneCount?.message)}
                </span>
              )}
            </div>
            <button
              type="button"
              className="btn btn-ghost self-start px-2"
              aria-label={`Remove location ${index + 1}`}
              disabled={fields.length === 1}
              onClick={() => remove(index)}
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="btn btn-ghost mt-3 text-sm"
        onClick={() => append({ name: '', phoneCount: '1' })}
      >
        + Add location
      </button>

      <p className="mt-3 text-sm font-medium" aria-live="polite">
        Total phones requested: {totalPhones}
      </p>
    </fieldset>
  );
};

export default PhoneHardwareFields;
